import { useState, useEffect } from 'react'
import { jobsService } from '../lib/supabase'

const TestSupabase = () => {
  const [status, setStatus] = useState('Testing connection...')
  const [categories, setCategories] = useState([])
  const [jobs, setJobs] = useState([])
  const [syncStatus, setSyncStatus] = useState(null)
  const [error, setError] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [clearing, setClearing] = useState(false)

  useEffect(() => {
    testConnection()
    fetchSyncStatus()
  }, [])

  const testConnection = async () => {
    try {
      setStatus('✅ Connected to Supabase!')
      
      // Test 1: Fetch categories
      setStatus('🔍 Fetching job categories...')
      const categoriesData = await jobsService.getCategories()
      setCategories(categoriesData)
      
      // Test 2: Fetch jobs
      setStatus('📋 Fetching jobs...')
      const jobsData = await jobsService.getJobs({ limit: 10 })
      setJobs(jobsData)
      
      setStatus('✅ All tests passed! Database is ready.')
      
    } catch (err) {
      console.error('Supabase test error:', err)
      setError(err.message)
      setStatus('❌ Connection failed')
    }
  }

  const fetchSyncStatus = async () => {
    try {
      const response = await fetch('/.netlify/functions/sync-status')
      const data = await response.json()
      
      if (data.success) {
        setSyncStatus(data.data)
      }
    } catch (err) {
      console.error('Failed to fetch sync status:', err)
    }
  }

  const triggerSync = async () => {
    setSyncing(true)
    setStatus('🔄 Triggering LogicMelon sync...')
    
    try {
      const response = await fetch('/.netlify/functions/trigger-sync', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      const result = await response.json()
      
      if (result.success) {
        setStatus('✅ Sync completed successfully!')
        // Refresh data
        setTimeout(() => {
          testConnection()
          fetchSyncStatus()
        }, 1000)
      } else {
        setStatus('❌ Sync failed: ' + result.error)
      }
      
    } catch (err) {
      console.error('Sync trigger error:', err)
      setStatus('❌ Failed to trigger sync: ' + err.message)
    } finally {
      setSyncing(false)
    }
  }

  const clearJobs = async () => {
    if (!confirm('⚠️ Are you sure you want to clear ALL jobs from the database? This cannot be undone!')) {
      return
    }

    setClearing(true)
    setStatus('🗑️ Clearing all jobs from database...')
    
    try {
      const response = await fetch('/.netlify/functions/clear-jobs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      
      const result = await response.json()
      
      if (result.success) {
        setStatus(`✅ Successfully cleared ${result.jobsCleared} jobs from database`)
        // Refresh data
        setTimeout(() => {
          testConnection()
          fetchSyncStatus()
        }, 1000)
      } else {
        setStatus('❌ Clear failed: ' + result.error)
      }
      
    } catch (err) {
      console.error('Clear jobs error:', err)
      setStatus('❌ Failed to clear jobs: ' + err.message)
    } finally {
      setClearing(false)
    }
  }

  const addTestJob = async () => {
    try {
      setStatus('➕ Adding test job...')
      
      // Note: This would normally be done through the admin interface
      // For now, we'll show what the data structure looks like
      const testJob = {
        logicmelon_id: 'test-' + Date.now(),
        title: 'Test HGV Driver Position',
        description: 'This is a test job created to verify the database connection.',
        location: 'London',
        salary_text: '£25,000 - £30,000',
        job_type: 'Full-time',
        category: 'HGV Driving',
        requirements: 'Valid HGV license, clean driving record',
        benefits: 'Company vehicle, health insurance'
      }
      
      console.log('Test job structure:', testJob)
      setStatus('✅ Test job structure ready (would be added via admin panel)')
      
    } catch (err) {
      console.error('Error creating test job:', err)
      setError(err.message)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">
            Supabase Database Test
          </h1>
          
          {/* Status */}
          <div className="mb-8 p-4 rounded-lg bg-blue-50 border border-blue-200">
            <h2 className="text-lg font-semibold text-blue-900 mb-2">Status</h2>
            <p className="text-blue-700">{status}</p>
            {error && (
              <p className="text-red-600 mt-2 font-medium">Error: {error}</p>
            )}
          </div>

          {/* Categories Test */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Job Categories ({categories.length})
            </h2>
            {categories.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="p-4 rounded-lg border-2 border-gray-200 hover:border-orange-500 transition-colors"
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-4 h-4 rounded-full"
                        style={{ backgroundColor: category.color }}
                      ></div>
                      <div>
                        <h3 className="font-medium text-gray-900">
                          {category.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {category.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">No categories found. Make sure you ran the SQL schema.</p>
            )}
          </div>

          {/* Sync Status */}
          {syncStatus && (
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">
                LogicMelon Sync Status
              </h2>
              <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                <div className="flex justify-between">
                  <span className="font-medium">Total Active Jobs:</span>
                  <span className="text-orange-600 font-bold">{syncStatus.totalActiveJobs}</span>
                </div>
                
                {syncStatus.lastSuccessfulSync && (
                  <div className="flex justify-between">
                    <span className="font-medium">Last Successful Sync:</span>
                    <span className="text-green-600">
                      {new Date(syncStatus.lastSuccessfulSync.completedAt).toLocaleString()}
                    </span>
                  </div>
                )}
                
                {syncStatus.latestSync && (
                  <div className="border-t pt-3">
                    <h3 className="font-medium mb-2">Latest Sync:</h3>
                    <div className="text-sm space-y-1">
                      <div className="flex justify-between">
                        <span>Status:</span>
                        <span className={`font-medium ${
                          syncStatus.latestSync.status === 'completed' ? 'text-green-600' :
                          syncStatus.latestSync.status === 'failed' ? 'text-red-600' :
                          'text-yellow-600'
                        }`}>
                          {syncStatus.latestSync.status}
                        </span>
                      </div>
                      {syncStatus.latestSync.jobsProcessed > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span>Jobs Processed:</span>
                            <span>{syncStatus.latestSync.jobsProcessed}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Added:</span>
                            <span className="text-green-600">{syncStatus.latestSync.jobsAdded}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Updated:</span>
                            <span className="text-blue-600">{syncStatus.latestSync.jobsUpdated}</span>
                          </div>
                        </>
                      )}
                      {syncStatus.latestSync.errorMessage && (
                        <div className="text-red-600 text-xs mt-2">
                          Error: {syncStatus.latestSync.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Jobs Test */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">
              Current Jobs ({jobs.length})
            </h2>
            {jobs.length > 0 ? (
              <div className="space-y-4">
                {jobs.map((job) => (
                  <div
                    key={job.id}
                    className="p-4 rounded-lg border border-gray-200 hover:border-orange-500 transition-colors"
                  >
                    <h3 className="font-semibold text-gray-900">{job.title}</h3>
                    <p className="text-gray-600">{job.location} • {job.job_type}</p>
                    <p className="text-orange-600 font-medium">{job.salary_text}</p>
                    <div className="flex space-x-4 text-sm text-gray-500 mt-2">
                      <span>Category: {job.category}</span>
                      <span>Posted: {new Date(job.posted_date).toLocaleDateString()}</span>
                      {job.view_count > 0 && <span>Views: {job.view_count}</span>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-500">
                No jobs found yet. Click "Sync Jobs" to fetch from LogicMelon.
              </p>
            )}
          </div>

          {/* Test Actions */}
          <div className="border-t pt-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Test Actions</h2>
            <div className="flex flex-wrap gap-4">
              <button
                onClick={testConnection}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                🔄 Retest Connection
              </button>
              <button
                onClick={triggerSync}
                disabled={syncing}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {syncing ? '⏳ Syncing...' : '🔄 Sync Jobs (1 job)'}
              </button>
              <button
                onClick={clearJobs}
                disabled={clearing}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {clearing ? '⏳ Clearing...' : '🗑️ Clear All Jobs'}
              </button>
              <button
                onClick={fetchSyncStatus}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                📊 Refresh Status
              </button>
              <button
                onClick={addTestJob}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                🧪 Test Job Structure
              </button>
            </div>
            
            {/* Testing Notes */}
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h3 className="font-semibold text-yellow-900 mb-2">🧪 Testing Mode</h3>
              <ul className="text-sm text-yellow-800 space-y-1">
                <li>• <strong>Sync Jobs</strong> - Fetches only 1 job from LogicMelon for testing</li>
                <li>• <strong>Clear Jobs</strong> - Removes all jobs from database (use for testing)</li>
                <li>• <strong>Production Mode</strong> - Change PageSize from 1 to 1000 in sync-jobs.js</li>
              </ul>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mt-8 p-4 rounded-lg bg-green-50 border border-green-200">
            <h3 className="text-lg font-semibold text-green-900 mb-2">✅ Next Steps</h3>
            <ul className="text-green-700 space-y-1">
              <li>• Database connection is working</li>
              <li>• Job categories are loaded</li>
              <li>• Ready to build LogicMelon sync function</li>
              <li>• Ready to build jobs page</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TestSupabase
